'use client'
import { useCWDocumentsContext } from '@components/shared/terms-and-conditions/CWDocumentsContext'
import { Button } from '@components/ui/button'
import { NavLink } from '@components/shared/nav-link/NavLink'
import { useNavMenu } from '../header/nav-menu/useNavMenu'
import { ReactElement, useState } from 'react'
import { SocialMediaElements } from './SocialMediaElements'
import trim from 'lodash/trim'
import { JoinMailingListDialog } from './JoinMailingListDialog'
import { paths } from '@router'
import { ChatbotOverlay } from '@components/shared/ChatbotOverlay'
import Image from 'next/image'

export const Footer = () => {
  const [isChatbotOpen, setIsChatbotOpen] = useState(false)
  // Use string literals directly to avoid circular dependency issues with NavItems enum
  // These match the NavItems enum values but avoid the import dependency
  const allowedNavPopup: string[] = ['Terms & Conditions', 'Privacy policy']
  const [isMailListPopupOpen, seMailListPopupOpen] = useState(false)
  const { setIsTermsAndConditionsModalOpen, setIsPrivacyPolicyModalOpen } =
    useCWDocumentsContext()

  const navMenu = useNavMenu({
    onTermsOfConditionsClick: () => setIsTermsAndConditionsModalOpen(true),
    onPrivacyPolicyClick: () => setIsPrivacyPolicyModalOpen(true),
  })
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(section)) {
        newSet.delete(section)
      } else {
        newSet.add(section)
      }
      return newSet
    })
  }

  const isSectionExpanded = (section: string) => {
    return expandedSections.has(section)
  }

  const handleChatbotClick = () => {
    setIsChatbotOpen(true)
  }

  const handleChatbotClose = () => {
    setIsChatbotOpen(false)
  }

  const renderExternalLinkItem = (text: string, href: string): ReactElement => (
    <a
      href={href}
      target="_blank"
      className="block text-base text-cw-purple-light/70 hover:text-cw-electric-purple transition-all duration-300 mb-2.5 last:mb-0 font-montserrat"
    >
      {text}
    </a>
  )

  const renderInternalLinkItem = (
    text: string,
    href: string,
    prefetch?: boolean,
  ): ReactElement => (
    <NavLink
      to={href}
      prefetch={prefetch}
      className="block text-base text-cw-purple-light/70 hover:text-cw-electric-purple transition-all duration-300 mb-2.5 last:mb-0 font-montserrat"
    >
      {text}
    </NavLink>
  )

  const renderSectionHeader = (title: string, section: string) => (
    <div
      className="flex items-center gap-2 cursor-pointer sm:cursor-default mb-2.5"
      onClick={() => toggleSection(section)}
    >
      <span
        className={`text-xl leading-none text-cw-electric-purple sm:hidden transition-transform duration-300 ${
          isSectionExpanded(section) ? 'rotate-45' : ''
        }`}
      >
        +
      </span>
      <h3 className="font-funnel uppercase text-lg font-semi-light text-cw-electric-purple">
        {title}
      </h3>
    </div>
  )

  return (
    <footer className="relative text-white overflow-hidden">
      <div className="absolute h-[45px] sm:h-[60px] left-0 bottom-[calc(100%-3rem)] sm:bottom-[calc(100%-4rem)] w-full bg-[repeat:no-repeat,repeat] bg-[length:150%,auto] sm:bg-[length:cover,auto] " />
      <div className="bg-cw-deep-purple-dark pb-8">
        <div className="container mx-auto px-6 pt-[50px] sm:pt-[70px]">
          <nav
            aria-label="Footer navigation"
            className="grid sm:grid-cols-7 grid-cols-1 gap-6 sm:gap-4 text-sm"
          >
            <div className="col-span-1 sm:col-span-2 mb-7 sm:mb-0">
              <Image
                src="/images/wlth/logo/wlth-logo-white.webp"
                alt="WLTH logo"
                width={120}
                height={23}
              />
              <div
                className="flex flex-col md:flex-row gap-3 mt-10"
                style={{ paddingRight: 70 }}
              >
                <a
                  href={paths.androidStoreUrl}
                  target="_blank"
                  className="w-48 md:w-[50%]"
                  aria-label="Download WLTH on Google Play"
                >
                  <Image
                    src="/images/wlth/store/download-google-play.webp"
                    alt="Google Play"
                    width={180}
                    height={53}
                    className="w-full h-auto"
                    loading="lazy"
                  />
                </a>
                <a
                  href={paths.iosStoreUrl}
                  target="_blank"
                  className="w-48 md:w-[50%]"
                  aria-label="Download WLTH on App Store"
                >
                  <Image
                    src="/images/wlth/store/download-app-store.webp"
                    alt="Apple Store"
                    width={180}
                    height={60}
                    className="w-full h-auto"
                    loading="lazy"
                  />
                </a>
              </div>
            </div>

            <div className="col-span-1 sm:col-span-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:border-b-0 pb-4 sm:pb-0">
                {renderSectionHeader('Investment', 'investment')}
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    isSectionExpanded('investment')
                      ? 'max-h-[500px] opacity-100'
                      : 'max-h-0 opacity-0'
                  } sm:max-h-[500px] sm:opacity-100`}
                >
                  {renderInternalLinkItem(
                    'Invest',
                    paths.funds.exclusiveAccess.companies,
                  )}
                  {renderInternalLinkItem(
                    'Funds',
                    paths.funds.exclusiveAccess.fundsOverview,
                  )}
                </div>
              </div>

              <div className="sm:border-b-0 pb-4 sm:pb-0">
                {renderSectionHeader('Features', 'features')}
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    isSectionExpanded('features')
                      ? 'max-h-[500px] opacity-100'
                      : 'max-h-0 opacity-0'
                  } sm:max-h-[500px] sm:opacity-100`}
                >
                  {renderInternalLinkItem('Portfolio', paths.portfolio.main, false)}
                  {renderExternalLinkItem('Slices', '/docs/investment/slices')}
                  {renderExternalLinkItem(
                    'Gifting',
                    '/docs/investment/slices/gifting-slices',
                  )}
                  {renderExternalLinkItem(
                    'Marketplace',
                    '/docs/investment/slices/buying-and-selling-slices',
                  )}
                  {renderExternalLinkItem('Fees', '/docs/investment/staking-and-fees')}
                </div>
              </div>

              {/* <div className="sm:border-b-0 pb-4 sm:pb-0">
                {renderSectionHeader('Learn', 'learn')}
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    isSectionExpanded('learn')
                      ? 'max-h-[500px] opacity-100'
                      : 'max-h-0 opacity-0'
                  } sm:max-h-[500px] sm:opacity-100`}
                >
                  {renderInternalLinkItem('What is WLTH?', '/what-is-wlth')}
                  {renderInternalLinkItem('Compare pre-IPO platforms', '/compare')}
                  {renderInternalLinkItem(
                    'Non-accredited investor guide',
                    '/learn/non-accredited-pre-ipo',
                  )}
                  {renderInternalLinkItem('Security & Hacken audits', '/security')}
                  {renderInternalLinkItem(
                    'Liquidity & lock-ups explained',
                    '/liquidity-lockups',
                  )}
                </div>
              </div> */}

              <div className="sm:border-b-0 pb-4 sm:pb-0">
                {renderSectionHeader('About', 'about')}
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    isSectionExpanded('about')
                      ? 'max-h-[500px] opacity-100'
                      : 'max-h-0 opacity-0'
                  } sm:max-h-[500px] sm:opacity-100`}
                >
                  {renderExternalLinkItem('About WLTH', '/docs/')}
                  {renderExternalLinkItem('Team', '/docs/team-and-contributors')}
                  {renderInternalLinkItem('Help & Support', paths.support)}
                  {renderExternalLinkItem('Brand Kit', '/docs/brand-kit')}
                  {renderExternalLinkItem(
                    'T&Cs',
                    '/docs/disclaimer/terms-and-conditions',
                  )}
                </div>
              </div>
            </div>
          </nav>

          <div className="grid grid-cols-1 sm:grid-cols-7 items-baseline mt-10 border-b sm:border-b-0 border-cw-lighter-grey-stroke pb-[50px] sm:pb-0 mb-10">
            <div className="flex flex-col gap-4 sm:pr-20 col-span-1 sm:col-span-2 h-full mt-auto">
              <Button
                variant={'green'}
                className="!text-base w-full xs:w-auto"
                onClick={() => seMailListPopupOpen(true)}
              >
                Join mailing list
              </Button>
            </div>

            <div className="col-span-5 flex flex-col h-full mt-4 sm:mt-0 mb-6 sm:mb-0">
              <h3 className="font-bold text-lg text-white font-funnel mt-auto">
                Disclaimer:
              </h3>
              <p className="mt-2 text-sm text-white/80 text-xs font-montserrat">
                WLTH’s platform and operations are fully compliant with all applicable
                laws, regulations and regulatory requirements of the Cook Islands. WLTH
                operates within the regulatory framework and under the guidance and
                supervisory oversight of the Cook Islands Financial Supervisory Commission
                (FSC). WLTH has obtained and maintains all requisite approvals, licences,
                registrations, consents and permissions necessary for the lawful conduct
                of its business activities in the Cook Islands. This includes receipt of a
                formal no-objection confirmation from the FSC in relation to the
                establishment and operation of the WLTH platform. WLTH continues to adhere
                to all ongoing compliance obligations, regulatory conditions and reporting
                requirements as may be prescribed by the FSC from time to time, and
                remains committed to maintaining the highest standards of regulatory
                compliance and governance in its operations.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-7 grid-cols-1 mt-8 text-sm">
            <div className="col-span-2 text-white/80 text-xs flex flex-col items-center sm:items-start order-3 sm:order-none justify-center xs:justify-start gap-1 sm:mt-[1.25rem] font-montserrat">
              <div>&copy; {new Date().getFullYear()} WLTH. All rights reserved.</div>
              <div className="text-[0.625rem]">
                Version: {trim(process.env.COMMIT_HASH || '')}
              </div>
            </div>

            <div className="flex gap-4 col-span-2 text-xs  flex items-center justify-between xs:justify-start font-montserrat">
              {navMenu
                .filter((item) => allowedNavPopup.includes(item.label))
                .map((item, index) => {
                  if (item.navLinkProps) {
                    const { to, ...restNavLinkProps } = item.navLinkProps
                    return (
                      <NavLink
                        key={index}
                        to={to}
                        onClick={() => {
                          if (item.onClick) {
                            item.onClick()
                          }
                        }}
                        target={item.target || '_self'}
                        {...restNavLinkProps}
                        className={
                          'text-cw-grey-grey-200 hover:text-cw-electric-purple transition-all duration-300'
                        }
                      >
                        {item.label}
                      </NavLink>
                    )
                  }
                  return (
                    <button
                      key={index}
                      onClick={() => {
                        if (item.onClick) {
                          item.onClick()
                        }
                      }}
                      className="min-h-6 min-w-6 px-2 py-1.5 text-cw-grey-grey-200 hover:text-cw-electric-purple transition-all duration-300 cursor-pointer"
                    >
                      {item.label}
                    </button>
                  )
                })}
            </div>

            <SocialMediaElements onChatbotClick={handleChatbotClick} />
          </div>
        </div>
      </div>
      <JoinMailingListDialog
        open={isMailListPopupOpen}
        onOpenChange={() => seMailListPopupOpen(false)}
      />
      <ChatbotOverlay isOpen={isChatbotOpen} onClose={handleChatbotClose} />
    </footer>
  )
}
